import { memo, useCallback, useEffect, useRef } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import clsx from 'clsx';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { TiptapToolbar } from './TiptapToolbar';
import {
  ImageUploadExtension,
  type ImageUploadStorage,
} from './extensions/ImageUpload';
import styles from './TiptapEditor.module.css';

interface TiptapEditorProps {
  content: string | null;
  onChange?: (jsonContent: string) => void;
  editable?: boolean;
  onImageUpload?: (file: File) => Promise<string>;
  placeholder?: string;
  variant?: 'compact' | 'expanded';
}

const HEADING_LEVELS: [1, 2, 3] = [1, 2, 3];

function parseContent(content: string | null): Record<string, unknown> | null {
  if (!content) return null;
  try {
    return JSON.parse(content) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export const TiptapEditor = memo(function TiptapEditor({
  content,
  onChange,
  editable = true,
  onImageUpload,
  placeholder = '',
  variant = 'compact',
}: TiptapEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: HEADING_LEVELS },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: styles.link },
      }),
      Underline,
      Image.configure({
        HTMLAttributes: { class: styles.image },
        resize: {
          enabled: true,
          directions: ['bottom-right', 'bottom-left', 'top-right', 'top-left'],
          minWidth: 50,
          minHeight: 50,
          alwaysPreserveAspectRatio: true,
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      ImageUploadExtension,
    ],
    content: parseContent(content),
    editable,
    // React 19 + StrictMode: defer creation to an effect so useEditor manages
    // the instance lifecycle (returns Editor | null) instead of a synchronous
    // instance that the dev double-invoke can leave destroyed.
    immediatelyRender: false,
  });

  const isProgrammaticUpdateRef = useRef(false);

  const handleUpdate = useCallback(
    ({ editor: editorInstance }: { editor: Editor }) => {
      if (onChange && !isProgrammaticUpdateRef.current) {
        const json = JSON.stringify(editorInstance.getJSON());
        onChange(json);
      }
    },
    [onChange]
  );

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.on('update', handleUpdate);
    return () => {
      editor.off('update', handleUpdate);
    };
  }, [editor, handleUpdate]);

  // Sync image upload handler into extension storage. Guarded against a
  // transient destroyed-editor instance (React StrictMode mount→unmount→
  // remount) where the extension storage is briefly cleared — the effect
  // re-runs with valid storage once useEditor recreates the editor.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const storage = (
      editor.storage as unknown as { imageUpload?: ImageUploadStorage }
    ).imageUpload;
    if (storage) {
      storage.uploadFn = onImageUpload ?? null;
    }
  }, [editor, onImageUpload]);

  // Update content when prop changes externally — suppress onChange
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (content !== null) {
      const currentContent = JSON.stringify(editor.getJSON());
      if (currentContent !== content) {
        const parsed = parseContent(content);
        if (parsed) {
          isProgrammaticUpdateRef.current = true;
          editor.commands.setContent(parsed);
          isProgrammaticUpdateRef.current = false;
        }
      }
    } else if (!editor.isEmpty) {
      isProgrammaticUpdateRef.current = true;
      editor.commands.clearContent();
      isProgrammaticUpdateRef.current = false;
    }
  }, [editor, content]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.setEditable(editable);
  }, [editor, editable]);

  return (
    <div
      className={clsx(
        styles.editorWrapper,
        variant === 'expanded' && styles.editorWrapperExpanded,
        !editable && styles.readOnly
      )}
    >
      {editable && editor && (
        <TiptapToolbar editor={editor} onImageUpload={onImageUpload} />
      )}
      <EditorContent
        editor={editor}
        className={clsx(
          styles.editorContent,
          variant === 'expanded' && styles.editorContentExpanded
        )}
      />
    </div>
  );
});
